/**
 * @file        src/lib/couponStatus.js
 * @description Live redemption status for a `coupon_codes` row — mirrors the
 *              REDEEMABLE predicate in booking_backend/models/couponCodeModel.js
 *              (is_active, the valid_from/valid_to window honoured through the
 *              END of the expiry day, and usage_limit 0/NULL = unlimited) so the
 *              admin console's Status column means the same thing as "can a
 *              customer use this right now". The generic lookup table used to
 *              show a coupon as "Active" purely from the `is_active` toggle,
 *              which stayed green long after valid_to passed or usage_limit was
 *              hit — an admin had no way to tell from the list that an offer's
 *              linked coupon had gone dead on the storefront.
 * @author      Kinetika Labs
 * @created     2026-08-10
 */
import { todayStr } from './dateRules';

/**
 * @param {object} row  a coupon_codes row (valid_from/valid_to as
 *   'YYYY-MM-DD HH:MM:SS' or date strings; usage_limit/used_count numeric)
 * @returns {{ key: 'active'|'inactive'|'scheduled'|'expired'|'exhausted', label: string, tone: 'ok'|'warn'|'pend'|'rej' }}
 */
export function couponStatus(row) {
  if (!row?.is_active) return { key: 'inactive', label: 'Inactive', tone: 'rej' };

  const today = todayStr();
  // Stored at 00:00:00 / 23:59:59 of the chosen day — a date-only compare keeps
  // the expiry day itself usable, matching the server's `DATE(valid_to) >= CURDATE()`.
  const from = row.valid_from ? String(row.valid_from).slice(0, 10) : null;
  const to = row.valid_to ? String(row.valid_to).slice(0, 10) : null;

  if (to && to < today) return { key: 'expired', label: 'Expired', tone: 'rej' };
  if (from && from > today) return { key: 'scheduled', label: 'Scheduled', tone: 'pend' };

  const limit = Number(row.usage_limit) || 0;
  const used = Number(row.used_count) || 0;
  if (limit > 0 && used >= limit) return { key: 'exhausted', label: 'Exhausted', tone: 'rej' };

  return { key: 'active', label: 'Active', tone: 'ok' };
}
